// Filiación nueva del paciente (Fase 2, 001 — ver PLAN.md). Constantes de extensión
// compartidas entre DatosDemograficosTab.tsx (captura) y Formulario001Page.tsx
// (bloqueo de firma) — un solo lugar para las `url`, nada de duplicarlas.
import type { Patient } from '@medplum/fhirtypes';
import { valorExtensionCoding, valorExtensionTexto } from '../lib/extensionesFhir';

export const EXT_BASE = 'http://logiacare.ec/fhir/extension/';
export const EXT_OCUPACION = `${EXT_BASE}ocupacion`;
export const EXT_NACIONALIDAD = `${EXT_BASE}nacionalidad`;
export const EXT_AUTOIDENTIFICACION_ETNICA = `${EXT_BASE}autoidentificacion-etnica`;
export const EXT_NACIONALIDAD_ETNICA = `${EXT_BASE}nacionalidad-etnica`;
export const EXT_PUEBLO = `${EXT_BASE}pueblo`;
export const EXT_NIVEL_EDUCACION = `${EXT_BASE}nivel-educacion`;
export const EXT_ESTADO_EDUCACION = `${EXT_BASE}estado-educacion`;
export const EXT_TIPO_EMPRESA = `${EXT_BASE}tipo-empresa`;
export const EXT_SEGURO_SALUD = `${EXT_BASE}seguro-salud`;
export const EXT_TIPO_BONO = `${EXT_BASE}tipo-bono`;
export const EXT_TIPO_CONTACTO_PACIENTE = `${EXT_BASE}tipo-contacto-paciente`;
export const CODIGO_REPRESENTANTE_LEGAL = 'representante-legal';

/** Extensión HL7 estándar, no `logiacare.ec` — ver PLAN.md, "001 — Lugar de nacimiento". */
export const EXT_LUGAR_NACIMIENTO = 'http://hl7.org/fhir/StructureDefinition/patient-birthPlace';

export const URL_CODESYSTEM_AUTOIDENTIFICACION_ETNICA = 'http://logiacare.ec/fhir/CodeSystem/autoidentificacion-etnica';

export function edadDe(birthDate: string | undefined): number | undefined {
  if (!birthDate) return undefined;
  const nacimiento = new Date(birthDate);
  if (Number.isNaN(nacimiento.getTime())) return undefined;
  const ahora = new Date();
  let edad = ahora.getFullYear() - nacimiento.getFullYear();
  const aunNoCumple = ahora.getMonth() < nacimiento.getMonth() || (ahora.getMonth() === nacimiento.getMonth() && ahora.getDate() < nacimiento.getDate());
  if (aunNoCumple) edad--;
  return edad;
}

/**
 * Campos de filiación (Anexo 1, instructivo del 001) que faltan en este `Patient` —
 * `[]` = completo. Cada entrada es una clave de i18n bajo `fichaPaciente.demograficos.*`
 * (mismo namespace que ya usan las etiquetas del formulario), para poder armar el
 * mensaje de bloqueo sin repetir texto. `nacionalidadEtnica` solo se exige si la
 * autoidentificación es "indigena"; `pueblo` solo si `nacionalidadEtnica` es "Kichwa"
 * — el resto de la ficha censal INEC no aplica a todos los pacientes por diseño del
 * propio instructivo, no es una omisión.
 */
export function camposFiliacionFaltantes(patient: Patient): string[] {
  const faltantes: string[] = [];
  const ext = patient.extension;

  if (!patient.identifier?.[0]?.type?.coding?.[0]?.code) faltantes.push('tipoDocumentoIdentificacion');
  if (!patient.extension?.find((e) => e.url === EXT_LUGAR_NACIMIENTO)?.valueAddress?.text) faltantes.push('lugarNacimiento');
  if (!valorExtensionTexto(ext, EXT_NACIONALIDAD)) faltantes.push('nacionalidad');

  const autoidentificacion = valorExtensionCoding(ext, EXT_AUTOIDENTIFICACION_ETNICA);
  if (!autoidentificacion) faltantes.push('autoidentificacionEtnica');
  const nacionalidadEtnica = valorExtensionTexto(ext, EXT_NACIONALIDAD_ETNICA);
  if (autoidentificacion?.code === 'indigena' && !nacionalidadEtnica) faltantes.push('nacionalidadEtnica');
  if (nacionalidadEtnica === 'Kichwa' && !valorExtensionTexto(ext, EXT_PUEBLO)) faltantes.push('pueblo');

  if (!valorExtensionTexto(ext, EXT_NIVEL_EDUCACION)) faltantes.push('nivelEducacion');
  if (!valorExtensionTexto(ext, EXT_TIPO_EMPRESA)) faltantes.push('tipoEmpresa');
  if (!valorExtensionTexto(ext, EXT_SEGURO_SALUD)) faltantes.push('seguroSalud');
  if (!valorExtensionTexto(ext, EXT_TIPO_BONO)) faltantes.push('tipoBono');

  const direccion = patient.address?.[0];
  if (!direccion?.state || !direccion?.city) faltantes.push('residenciaEstructurada');

  const edad = edadDe(patient.birthDate);
  if (edad !== undefined && edad < 5) {
    const tieneRepresentante = patient.contact?.some((c) => c.extension?.some((e) => e.url === EXT_TIPO_CONTACTO_PACIENTE && e.valueCode === CODIGO_REPRESENTANTE_LEGAL));
    if (!tieneRepresentante) faltantes.push('representanteLegal');
  }

  return faltantes;
}
